import { useState, useMemo, useCallback, useEffect } from "react";
import { Product, ProductItem } from "../../api-client";
import default_image from "../../assets/png/no-image.png";

interface UseDetailStateOptions {
  productDetail: Product;
  productItems: ProductItem[];
  onStockUpdate?: (colorId: number, sizeId: number, newStock: number) => void;
}

export function useDetailState({
  productDetail,
  productItems,
  onStockUpdate,
}: UseDetailStateOptions) {
  const getItemColorId = useCallback(
    (item: ProductItem) => {
      const distribution: any = (item as any).distribution;
      const distributionColor = distribution?.color;
      const distributionColorId =
        typeof distributionColor === "number"
          ? distributionColor
          : distributionColor?.id;

      return (
        distributionColorId ??
        distribution?.color_id ??
        (item as any).color?.id ??
        (item as any).color_id ??
        null
      );
    },
    [],
  );

  const getItemSizeId = useCallback(
    (item: ProductItem) => {
      const distribution: any = (item as any).distribution;
      const distributionSize = distribution?.size;
      const distributionSizeId =
        typeof distributionSize === "number"
          ? distributionSize
          : distributionSize?.id;

      return (
        distributionSizeId ??
        distribution?.size_id ??
        (item as any).size?.id ??
        (item as any).size_id ??
        null
      );
    },
    [],
  );

  const getItemStock = useCallback(
    (item: ProductItem) => {
      const distribution: any = (item as any).distribution;
      return distribution?.stock ?? (item as any).stock ?? 0;
    },
    [],
  );

  const getItemColor = useCallback(
    (item: ProductItem) => {
      const distribution: any = (item as any).distribution;
      const distributionColor = distribution?.color;

      if (distributionColor && typeof distributionColor === "object") {
        return distributionColor;
      }

      const colorId =
        (typeof distributionColor === "number" ? distributionColor : undefined) ??
        distribution?.color_id ??
        (item as any).color?.id ??
        (item as any).color_id;

      if (colorId == null) {
        return (item as any).color ?? null;
      }

      return (productDetail.colors ?? []).find((color) => color.id === colorId) ?? null;
    },
    [productDetail.colors],
  );

  const getItemSize = useCallback(
    (item: ProductItem) => {
      const distribution: any = (item as any).distribution;
      const distributionSize = distribution?.size;

      if (distributionSize && typeof distributionSize === "object") {
        return distributionSize;
      }

      const sizeId =
        (typeof distributionSize === "number" ? distributionSize : undefined) ??
        distribution?.size_id ??
        (item as any).size?.id ??
        (item as any).size_id;

      if (sizeId == null) {
        return (item as any).size ?? null;
      }

      return (productDetail.sizes ?? []).find((size) => size.id === sizeId) ?? null;
    },
    [productDetail.sizes],
  );

  const [selectedState, setSelectedState] = useState({
    quantity: 1,
    sizeId: null as number | null,
    colorId: null as number | null,
    stock: 0,
  });

  const [modalState, setModalState] = useState({
    isOpen: false,
    imageSrc: default_image,
  });

  const handleStockUpdate = useCallback(
    (colorId: number, sizeId: number, newStock: number) => {
      setSelectedState((prev) => ({
        ...prev,
        stock: newStock,
        quantity: prev.quantity > newStock ? newStock : prev.quantity,
      }));
      onStockUpdate?.(colorId, sizeId, newStock);
    },
    [onStockUpdate],
  );

  const inStockProductItems = useMemo(
    () =>
      productItems.filter(
        (item) =>
          getItemStock(item) > 0 &&
          getItemColorId(item) !== null &&
          getItemSizeId(item) !== null,
      ),
    [getItemColorId, getItemSizeId, getItemStock, productItems],
  );

  const selectableProductItems = useMemo(
    () =>
      productItems.filter(
        (item) =>
          getItemColorId(item) !== null &&
          getItemSizeId(item) !== null,
      ),
    [getItemColorId, getItemSizeId, productItems],
  );

  const filteredProductItems = useMemo(() => {
    if (selectedState.colorId == null) return productItems;
    return productItems.filter(
      (item) => getItemColorId(item) === selectedState.colorId,
    );
  }, [getItemColorId, selectedState.colorId, productItems]);

  const availableSizeIds = useMemo(() => {
    if (selectedState.colorId == null) {
      return new Set(
        inStockProductItems
          .map((item) => getItemSizeId(item))
          .filter((id): id is number => id !== null),
      );
    }

    return new Set(
      filteredProductItems
        .filter((item) => getItemStock(item) > 0)
        .map((item) => getItemSizeId(item))
        .filter((id): id is number => id !== null),
    );
  }, [
    selectedState.colorId,
    filteredProductItems,
    inStockProductItems,
    getItemSizeId,
    getItemStock,
  ]);

  const displayColors = useMemo(() => {
    if ((productDetail.colors ?? []).length > 0) {
      return productDetail.colors ?? [];
    }

    const colorMap = new Map<number, NonNullable<Product["colors"]>[number]>();

    productItems.forEach((item) => {
      const color = getItemColor(item);
      if (color?.id !== undefined && !colorMap.has(color.id)) {
        colorMap.set(color.id, color);
      }
    });

    return Array.from(colorMap.values());
  }, [getItemColor, productDetail.colors, productItems]);

  const displaySizes = useMemo(() => {
    if ((productDetail.sizes ?? []).length > 0) {
      return productDetail.sizes ?? [];
    }

    const sizeMap = new Map<number, NonNullable<Product["sizes"]>[number]>();

    productItems.forEach((item) => {
      const size = getItemSize(item);
      if (size?.id !== undefined && !sizeMap.has(size.id)) {
        sizeMap.set(size.id, size);
      }
    });

    return Array.from(sizeMap.values());
  }, [getItemSize, productDetail.sizes, productItems]);

  const availableColors = useMemo(() => {
    const colorIds = new Set(
      selectableProductItems
        .map((item) => getItemColorId(item))
        .filter(Boolean),
    );
    return displayColors.filter(
      (color) => color.id !== undefined && colorIds.has(color.id),
    );
  }, [displayColors, selectableProductItems, getItemColorId]);

  useEffect(() => {
    const sourceItems =
      inStockProductItems.length > 0 ? inStockProductItems : selectableProductItems;

    if (sourceItems.length === 0) {
      return;
    }

    setSelectedState((prev) => {
      let nextColorId = prev.colorId;
      let nextSizeId = prev.sizeId;

      if (nextColorId === null) {
        nextColorId = getItemColorId(sourceItems[0]);
      }

      const colorItems = sourceItems.filter(
        (item) => getItemColorId(item) === nextColorId,
      );

      const hasCurrentSizeInColor = colorItems.some(
        (item) => getItemSizeId(item) === nextSizeId,
      );

      if (nextSizeId === null || !hasCurrentSizeInColor) {
        nextSizeId = colorItems[0] ? getItemSizeId(colorItems[0]) : null;
      }

      const selectedItem = sourceItems.find(
        (item) =>
          getItemColorId(item) === nextColorId &&
          getItemSizeId(item) === nextSizeId,
      );

      const nextStock = selectedItem ? getItemStock(selectedItem) : 0;

      if (
        prev.colorId === nextColorId &&
        prev.sizeId === nextSizeId &&
        prev.stock === nextStock
      ) {
        return prev;
      }

      return {
        ...prev,
        colorId: nextColorId,
        sizeId: nextSizeId,
        stock: nextStock,
        quantity: nextStock > 0 ? 1 : prev.quantity,
      };
    });
  }, [
    inStockProductItems,
    selectableProductItems,
    getItemColorId,
    getItemSizeId,
    getItemStock,
  ]);

  const carouselImages = useMemo(() => {
    const images = selectedState.colorId
      ? productItems
          .filter(
            (item) => getItemColorId(item) === selectedState.colorId,
          )
          .flatMap((item) => item.images?.map((img) => img.image) ?? [])
      : productItems.flatMap(
          (item) => item.images?.map((img) => img.image) ?? [],
        );
    const validImages = images.filter(Boolean);
    return validImages.length > 0
      ? validImages
      : [productItems[0]?.images?.[0]?.image ?? productDetail.image ?? default_image];
  }, [
    selectedState.colorId,
    productItems,
    productDetail.image,
    getItemColorId,
  ]);

  const handleSelectColor = useCallback((colorId: number) => {
    setSelectedState((prev) => {
      const colorItemsInStock = productItems.filter(
        (item) =>
          getItemColorId(item) === colorId && getItemStock(item) > 0,
      );

      const colorItemsAnyStock = productItems.filter(
        (item) => getItemColorId(item) === colorId,
      );

      const sourceItems = colorItemsInStock.length > 0 ? colorItemsInStock : colorItemsAnyStock;

      const nextSizeId = sourceItems[0] ? getItemSizeId(sourceItems[0]) : null;
      const nextStock = sourceItems[0] ? getItemStock(sourceItems[0]) : 0;

      return {
        ...prev,
        colorId,
        sizeId: nextSizeId,
        stock: nextStock,
        quantity: 1,
      };
    });
  }, [productItems, getItemColorId, getItemSizeId, getItemStock]);

  const handleSelectSize = useCallback(
    (sizeId: number) => {
      setSelectedState((prev) => {
        if (!availableSizeIds.has(sizeId)) return prev;
        const selectedItem = productItems.find(
          (item) =>
            getItemColorId(item) === prev.colorId &&
            getItemSizeId(item) === sizeId,
        );

        const newState = {
          ...prev,
          sizeId,
          quantity: 1,
          stock: selectedItem ? getItemStock(selectedItem) : 0,
        };

        return newState;
      });
    },
    [productItems, availableSizeIds, getItemColorId, getItemSizeId, getItemStock],
  );

  const handleQuantityChange = useCallback((newQuantity: number) => {
    setSelectedState((prev) => ({
      ...prev,
      quantity: Math.max(1, Math.min(newQuantity, prev.stock || 1)),
    }));
  }, []);

  const handleModalOpen = useCallback((imageSrc: string) => {
    setModalState({ isOpen: true, imageSrc });
  }, []);

  const handleModalClose = useCallback(() => {
    setModalState((prev) => ({ ...prev, isOpen: false }));
  }, []);

  return {
    selectedState,
    modalState,
    displayColors,
    displaySizes,
    availableSizeIds,
    availableColors,
    carouselImages,
    handleStockUpdate,
    handleSelectColor,
    handleSelectSize,
    handleQuantityChange,
    handleModalOpen,
    handleModalClose,
  };
}
